import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL, ASSET_BASE_URL } from '../config/api';
import { getAuthHeaders, clearAuth } from '../utils/auth';
import { resolveUploadUrl } from '../utils/urls';
import type { ApiResponse, VideoDescriptor } from '../types/api';
import { getVideoCoverUrl, handleCoverError } from '../utils/videoCover';
import AppShell from './AppShell';
import { Select, SelectItem } from './Select';
import { Input } from './Input';
import { Lock, Globe, CheckCircle, Play } from 'lucide-react';
import './VideoEdit.css';

const VideoEdit: React.FC = () => {
  const [video, setVideo] = useState<VideoDescriptor | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // 编辑状态
  const [editingName, setEditingName] = useState<string>('');
  const [editingVisibility, setEditingVisibility] = useState<'private' | 'public'>('private');
  const [editingWordMode, setEditingWordMode] = useState<'raw' | 'display'>('raw');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [realigning, setRealigning] = useState(false);
  const [realignError, setRealignError] = useState('');
  const [realignSuccessDetail, setRealignSuccessDetail] = useState<string | null>(null);
  const realignSuccessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // 从URL参数或location state中获取videoId
    const params = new URLSearchParams(location.search);
    const videoId = params.get('videoId');

    if (videoId) {
      loadVideo(videoId);
    } else {
      navigate('/profile');
    }
  }, [location, navigate]);

  useEffect(() => {
    return () => {
      if (realignSuccessTimerRef.current) {
        clearTimeout(realignSuccessTimerRef.current);
        realignSuccessTimerRef.current = null;
      }
    };
  }, []);

  const loadVideo = async (videoId: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/video/${videoId}`, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      if (!response.ok) {
        throw new Error('获取视频信息失败');
      }

      const data: ApiResponse<VideoDescriptor> = await response.json();
      if (data.success && data.data) {
        setVideo(data.data);
        setEditingName(data.data.name || data.data.videoId);
        setEditingVisibility(data.data.visibility || 'private');
        setEditingWordMode(data.data.wordHighlightMode === 'raw' ? 'raw' : 'display');
      } else {
        window.alert(data.error || '获取视频信息失败');
        navigate('/video-management');
      }
    } catch (err) {
      console.error('加载视频失败:', err);
      window.alert('网络错误，请稍后重试');
      navigate('/video-management');
    } finally {
      setLoading(false);
    }
  };

  // Cover upload
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [localCoverPreview, setLocalCoverPreview] = useState<string | null>(null);

  const handleCoverClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCoverSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !video) return;

    // Preview immediately
    const previewUrl = URL.createObjectURL(file);
    setLocalCoverPreview(previewUrl);
    setUploadingCover(true);

    try {
      const form = new FormData();
      form.append('videoId', video.videoId);
      form.append('cover', file);

      const headers = { ...(getAuthHeaders() as Record<string, string>) };
      // Remove Content-Type so the browser will set multipart/form-data with boundary
      delete headers['Content-Type'];

      const resp = await fetch(`${API_BASE_URL}/video/update-cover`, {
        method: 'POST',
        headers,
        body: form,
      });

      if (resp.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      const result: ApiResponse<{ coverUrl?: string }> = await resp.json();
      if (result.success) {
        // refresh video data if backend provides coverUrl or re-load
        if (result.data?.coverUrl) {
          setVideo(prev => prev ? { ...prev, coverUrl: result.data!.coverUrl } : prev);
        } else {
          // reload full video descriptor
          await loadVideo(video.videoId);
        }
        setSuccessMessage('封面已更新');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setLocalCoverPreview(null);
        window.alert(result.error || '封面上传失败');
      }
    } catch (err) {
      console.error('封面上传失败', err);
      setLocalCoverPreview(null);
      window.alert('上传失败，请稍后重试');
    } finally {
      setUploadingCover(false);
      // clear file input value so selecting same file again will trigger change
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!video) return;

    if (!editingName.trim()) {
      window.alert('视频名称不能为空');
      return;
    }

    setUpdating(true);

    try {
      // 先更新名称
      const nameResponse = await fetch(`${API_BASE_URL}/video/update-name`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId: video.videoId,
          name: editingName.trim()
        }),
      });

      if (nameResponse.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }

      const nameData: ApiResponse = await nameResponse.json();
      if (!nameData.success) {
        window.alert(nameData.error || '更新名称失败');
        return;
      }

      // 再更新可见性
      const visibilityResponse = await fetch(`${API_BASE_URL}/video/update-visibility`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId: video.videoId,
          visibility: editingVisibility
        }),
      });

      const visibilityData: ApiResponse = await visibilityResponse.json();
      if (!visibilityData.success) {
        window.alert(visibilityData.error || '更新可见性失败');
        return;
      }

      // 词级对齐数据源（如果发生变化才请求）
      if ((video.wordHighlightMode ?? 'raw') !== editingWordMode) {
        const wordModeResp = await fetch(`${API_BASE_URL}/video/update-word-highlight-mode`, {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            videoId: video.videoId,
            mode: editingWordMode,
          }),
        });
        const wordModeData: ApiResponse = await wordModeResp.json();
        if (!wordModeData.success) {
          window.alert(wordModeData.error || '更新词级对齐失败');
          return;
        }
        // 本地同步，避免下一次比较还判定为脏
        setVideo(prev => prev ? { ...prev, wordHighlightMode: editingWordMode } : prev);
      }

      // 更新成功，显示成功消息
      setSuccessMessage('保存成功');
      // 3秒后自动清除成功消息
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      window.alert('网络错误，请稍后重试');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  const handleRealignWords = async () => {
    if (!video) return;
    const ok = window.confirm(
      '将基于磁盘上已保存的原文 VTT 重新跑 WhisperX 强制对齐，并重新生成 display_words.json。\n' +
        '请确认已在「编辑字幕」中保存过 VTT。长视频可能需要较长时间。继续吗？',
    );
    if (!ok) return;

    setRealignError('');
    setRealignSuccessDetail(null);
    if (realignSuccessTimerRef.current) {
      clearTimeout(realignSuccessTimerRef.current);
      realignSuccessTimerRef.current = null;
    }
    setRealigning(true);
    try {
      const resp = await fetch(resolveUploadUrl('video/subtitles/realign-words'), {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ videoId: video.videoId }),
      });
      if (resp.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      const data: ApiResponse<{ segCount?: number; elapsedMs?: number }> = await resp.json();
      if (!data.success) {
        setRealignError(data.error || '词级重对齐失败');
        return;
      }
      const secs = data.data?.elapsedMs ? (data.data.elapsedMs / 1000).toFixed(1) : '?';
      const seg = data.data?.segCount ?? '?';
      setRealignSuccessDetail(`已处理 ${seg} 句字幕词级对齐，用时 ${secs} 秒。`);
      if (realignSuccessTimerRef.current) {
        clearTimeout(realignSuccessTimerRef.current);
      }
      realignSuccessTimerRef.current = setTimeout(() => {
        setRealignSuccessDetail(null);
        realignSuccessTimerRef.current = null;
      }, 12000);
    } catch (err) {
      setRealignError(err instanceof Error ? err.message : String(err));
    } finally {
      setRealigning(false);
    }
  };

  if (loading) {
    return (
      <div className="video-edit-container">
        <div className="loading-message">加载中...</div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="video-edit-container">
        <div className="error-message">视频不存在</div>
      </div>
    );
  }

  const header = (
    <div className="video-edit-header app-shell-header-content">
      <button
        onClick={handleCancel}
        className="back-button"
        title="取消并返回"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
      </button>
      <h1>编辑视频</h1>
    </div>
  );

  return (
    <AppShell header={header} footer={null}>
      <div className="video-edit-container">
        <div className="video-edit-content">
        {/* 视频封面 */}
        <div className="video-cover-section">
          <div className="video-cover-large">
            <img
              src={localCoverPreview || getVideoCoverUrl(video)}
              alt={video.name || video.videoId}
              onError={handleCoverError}
              onClick={handleCoverClick}
              role="button"
              tabIndex={0}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleCoverSelected}
            />
            <div className="cover-edit-hint" onClick={handleCoverClick}>
              {uploadingCover ? '上传中...' : '更换封面'}
            </div>
            {video.capabilities?.hasWordAlignment && (
              <div className="video-badge-large">
                <span className="icon-inline" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l2 5 5 .5-4 3 1 5-4-2-4 2 1-5-4-3 5-.5z"></path></svg></span>
                <span>词级对齐</span>
              </div>
            )}
          </div>
        </div>

        {/* 编辑表单 */}
        <div className="edit-form">
          <div className="form-group">
            <label className="form-label">视频ID</label>
            <div className="video-id-display">{video.videoId}</div>
          </div>

          <div className="form-group">
            <label className="form-label">视频名称</label>
            <Input
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              placeholder="请输入视频名称"
              disabled={updating}
            />
          </div>

          <div className="form-group">
            <label className="form-label">可见性</label>
            <Select
              value={editingVisibility}
              onValueChange={(value) => setEditingVisibility(value as 'private' | 'public')}
              disabled={updating}
            >
              <SelectItem value="private"><Lock size={14} className="icon-inline" /> 私有</SelectItem>
              <SelectItem value="public"><Globe size={14} className="icon-inline" /> 公开</SelectItem>
            </Select>
          </div>

          <div className="form-group" style={{ marginTop: 24 }}>
            <label className="form-label">字幕</label>
            <button
              type="button"
              className="subtitle-edit-link"
              onClick={() => navigate(`/video/edit/subtitles?videoId=${encodeURIComponent(video.videoId)}`)}
              disabled={updating}
            >
              编辑字幕 →
            </button>
          </div>

          <div className="form-group" style={{ marginTop: 24 }}>
            <label className="form-label">词级对齐</label>
            {video.capabilities?.hasWordAlignment ? (
              <div className="word-mode-options">
                <label className={`word-mode-option ${editingWordMode === 'raw' ? 'is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="wordHighlightMode"
                    value="raw"
                    checked={editingWordMode === 'raw'}
                    onChange={() => setEditingWordMode('raw')}
                    disabled={updating}
                  />
                  <div className="word-mode-option-body">
                    <div className="word-mode-option-title">原始词级模式</div>
                    <div className="word-mode-option-desc">
                      使用 <code>words.json</code>（WhisperX 原始词级对齐，逐 token 高亮；新视频默认）
                    </div>
                  </div>
                </label>
                <label className={`word-mode-option ${editingWordMode === 'display' ? 'is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="wordHighlightMode"
                    value="display"
                    checked={editingWordMode === 'display'}
                    onChange={() => setEditingWordMode('display')}
                    disabled={updating}
                  />
                  <div className="word-mode-option-body">
                    <div className="word-mode-option-title">修复词级模式</div>
                    <div className="word-mode-option-desc">
                      使用 <code>display_words.json</code>（合并送り仮名/助词等，对日语阅读更友好）
                    </div>
                  </div>
                </label>
              </div>
            ) : null}
            <button
              type="button"
              className="subtitle-edit-link"
              title="基于已保存的 VTT 重新跑 WhisperX 强制对齐，重生成 display_words.json"
              onClick={handleRealignWords}
              disabled={updating || realigning}
            >
              {realigning ? '词级对齐中…' : '词级重对齐'}
            </button>
            {realignError ? (
              <div className="video-edit-realign-error" role="alert">
                {realignError}
              </div>
            ) : null}
            {realignSuccessDetail ? (
              <div className="video-edit-realign-result" role="status" aria-live="polite">
                <div className="video-edit-realign-result-row">
                  <CheckCircle
                    className="video-edit-realign-result-icon"
                    size={24}
                    strokeWidth={2}
                    aria-hidden
                  />
                  <div className="video-edit-realign-result-text">
                    <p className="video-edit-realign-result-title">词级重对齐已完成</p>
                    <p className="video-edit-realign-result-detail">{realignSuccessDetail}</p>
                    <p className="video-edit-realign-result-hint">
                      可以前往播放器查看最新的逐词高亮效果。
                    </p>
                    <button
                      type="button"
                      className="video-edit-realign-play"
                      onClick={() =>
                        navigate(
                          `/player?video=${encodeURIComponent(video.videoId)}&from=edit`,
                        )
                      }
                    >
                      <Play size={18} className="icon-inline" aria-hidden />
                      <span>前往播放器</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {successMessage && <div className="success-message">{successMessage}</div>}

          <div className="form-actions">
            <button
              onClick={handleSave}
              className="save-button"
              disabled={updating}
            >
              {updating ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
        </div>
      </div>
    </AppShell>
  );
};

export default VideoEdit;
